import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types';

import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  FormHelperText,
  MenuItem,
  Paper,
  RadioGroup,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { LoadingButton } from '@mui/lab';

import CustomModal from '../../custom/CustomModal';
import { CustomSelect } from '../../custom/CustomSelect';
import {
  capitaliseText,
  extractFormValues,
  isNotNullValue,
  isNullValue,
  validateForm
} from '../../../utils/formUtil';
import {
  AllocatedBox,
  AvailableBox,
  CapacityBox,
  OrderBox,
  PromisedBox,
  SupplierBox
} from '../../ui/styled-views';
import API from '../../../data/api';
import { fNumber } from '../../../utils/formatNumber';
import palette from '../../../theme/palette';
import RadioButton from 'src/components/ui/RadioButton';
import CustomTextField from 'src/components/custom/CustomTextField';

const CLIENT = 'client';

const initAssignment = {
  destination: { value: '', required: true, error: false, errorMessage: 'Choose if to assign remaining volume to client or depot' },
  clients: { value: [], required: true, error: false, errorMessage: 'Please assign client that is not more than remaining volume' },
  depot: { value: "", required: true, error: false, errorMessage: 'Please select a depot' },
  tank: { value: "", required: true, error: false, errorMessage: 'Please specify a tank' },
  volume: { value: "", required: true, error: false, errorMessage: 'Please specify correct volume to assign' },
  note: { value: "", required: false, error: false, },
};

function AssignEnrouteTruck({
  subOrder,
  clients = [],
  openModal,
  handleCloseModal,
  handleSubmitCallback
}) {

  const [submitted, setSubmitted] = useState(false);
  const [assignment, setAssignment] = useState(null);
  const [remainingVolume, setRemainingVolume] = useState(0);
  const [allocatedVolume, setAllocatedVolume] = useState(0);
  const [availableVolume, setAvailableVolume] = useState(0);
  const [depots, setDepots] = useState([]);
  const [tanks, setTanks] = useState([]);

  useEffect(() => {
    if (openModal) {
      getDepots();
      setAssignment({ ...initAssignment });
      setAllocatedVolume(subOrder?.allocated_volume || 0);
      setRemainingVolume(subOrder?.available_volume || 0);
    }

  }, [openModal, subOrder, clients]);

  const handleSubmitAssignment = async () => {

    // If any of the inputs or required parameters is empty, return
    if (!validateForm(setAssignment, assignment) || isNullValue(subOrder?.suborder_id)) {

      console.log("Opps validation error", assignment)
      return;
    }

    const { destination } = extractFormValues(assignment);
    setSubmitted(true);

    if (destination === CLIENT) {
      assignProductToClient();
    } else {

      assignProductToDepot();
    }

    setSubmitted(false);
  };

  const assignProductToClient = async () => {

    const { clients } = extractFormValues(assignment);
    let endpoint = `inventory/logistics/assign_trucks_enroute`
      .concat(`?supplier_suborder_id=${subOrder?.suborder_id}`)
    // .concat(`&client_suborder_ids=[${clients}]`)

    try {
      const response = await API.post(endpoint, {
        client_suborder_ids: [clients]
      });

      if (response.status === 200 || response.status === 206) {
        handleSubmitCallback();
        handleResetAndClose();
      } else {
        console.error('Error creating suborder assignment: ', response.status);
      }
    } catch (error) {
      console.error('Error creating suborder assignment: ', error);
    }
    setSubmitted(false);
  };

  const assignProductToDepot = async () => {

    const { volume, depot, tank, note} = extractFormValues(assignment);
    const endpoint = `inventory/logistics/assign_product_to_depot`;
    try {
      const response = await API.post(endpoint, {
        client_suborder_id: subOrder?.suborder_id,
        product_id: subOrder?.product_id,
        depot_id: depot,
        tank_id: tank,
        volume,
        note: note || null,
      });

      if (response.status === 200 || response.status === 206) {
        handleSubmitCallback();
        handleResetAndClose();
      } else {
        console.error('Error creating suborder assignment: ', response.status);
      }
    } catch (error) {
      console.error('Error creating suborder assignment: ', error);
    }
    setSubmitted(false);
  };

  const getDepots = async () => {
    let _depots = [];
    try {
      const response = await API.get('inventory/backoffice/depots?depot_type=storage');
      if (response.status === 200) {
        _depots = response.data.depots;
      } else {
        console.error('Error fetching depots: ', response.status);
      }
    } catch (error) {
      console.error('Error fetching depots: ', error);
    }
    setDepots(_depots);
  };

  const getDepotTanks = async (depot_id) => {
    if (isNullValue(depot_id)) {
      return;
    }

    let _tanks = [];
    try {
      const response = await API.get(`inventory/logistics/tanks?depot_id=${depot_id}`);
      if (response.status === 200) {
        _tanks = response.data
      } else {
        console.error('Error fetching depot tanks: ', response.status);
      }
    } catch (error) {
      console.error('Error fetching depot tanks: ', error);
    }
    setTanks(_tanks);
  };

  const handleInputChange = (field, value, error = false) => {
    const _assignment = {
      ...assignment,
      [field]: {
        ...assignment[field],
        value: value,
        error: isNullValue(value) || error,
      },
    }
    setAssignment(_assignment);
  };

  const handleClientAssignment = (client_order_id) => {
    const client = clients?.find((c) => c.suborder_id === client_order_id);
    const error = client.available_volume > subOrder?.available_volume;

    if (!error) {
      const remaining = subOrder?.available_volume - client.available_volume;
      const allocated = subOrder?.allocated_volume + client.available_volume;

      setRemainingVolume(remaining);
      setAllocatedVolume(allocated);
    }

    handleInputChange('clients', client_order_id, error)
  };

  const handleDestinationSelection = (event) => {
    const { value } = event.target;
    const _assignment = {
      ...initAssignment,
      destination: {
        ...initAssignment.destination,
        value: value,
        error: false,
      }
    }
    if (value === CLIENT) {
      delete _assignment.depot;
      delete _assignment.volume;
      delete _assignment.tank;

    } else {
      delete _assignment.clients;
    }
    setAssignment(_assignment);
  };

  const handleDepotSelection = (depotId) => {

    getDepotTanks(depotId);

    handleInputChange('depot', depotId, isNullValue(depotId))
  };

  const handleTankSelection = (tankId) => {

    const selectedTank = tanks.find((t) => t.tank_id === tankId);
    setAvailableVolume(selectedTank.capacity - (selectedTank.available_volume + selectedTank?.promised_volume));

    handleInputChange('tank', tankId, isNullValue(tankId))
  };

  const handleVolumeAssignment = (value) => {
    const error = (value < 1 || value > availableVolume || value > remainingVolume);
    const message = error ? `Please specify volume and cannot be more than ${remainingVolume}` : null;
    handleInputChange('volume', value, error, message);

    let _remaining = subOrder?.available_volume;
    if (!error) {
      _remaining = subOrder?.available_volume - value;
      // const _allocated = Number(subOrder?.allocated_volume) + Number(value);
      // setAllocatedVolume(_allocated);
    }

    setRemainingVolume(_remaining);

    handleInputChange('volume', value, error, message);
  };

  const handleResetAndClose = () => {
    setRemainingVolume(0);
    setAvailableVolume(0);
    setDepots([]);
    setTanks([]);
    handleCloseModal();
  }

  return (
    <Paper>
      <CustomModal
        openModal={openModal}
        handleCloseModal={handleResetAndClose}
        modalTitle={`Assign Truck
            ${assignment?.destination.value ? 'to ' + capitaliseText(assignment?.destination.value) : ''}
        `}
      >
        <>
          <Typography variant="inherit" sx={{ color: palette.grey[500], my: 2 }}>
            {`${(isNotNullValue(subOrder) && !subOrder?.from_depot) ? subOrder?.transporter_name + ', '
              + subOrder?.vehicle_registration + ', ' + subOrder?.driver_name : ''} `}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', my: 2 }}>
            <Card sx={{ minWidth: 100, mx: 1, backgroundColor: 'orange', color: 'white' }}>
              <CardContent>
                <Typography variant="h6"> Product: </Typography>
                <Typography variant="body">{subOrder?.fuel_type}</Typography>
              </CardContent>
            </Card>
            <Card sx={{ minWidth: 100, mx: 1, backgroundColor: 'purple', color: 'white' }}>
              <CardContent>
                <Typography variant="h6"> Allocated Volume:  </Typography>
                <Typography variant="body">{fNumber(allocatedVolume)}</Typography>
              </CardContent>
            </Card>
            {/* <Card sx={{ minWidth: 100, mx: 1, backgroundColor: 'lightgreen', color: 'white' }}>
              <CardContent>
                <Typography variant="h6">  Available Volume: </Typography>
                <Typography variant="body"> {fNumber(remainingVolume)} </Typography>
              </CardContent>
            </Card> */}
            <Card sx={{ minWidth: 100, mx: 1, backgroundColor: 'black', color: 'white' }}>
              <CardContent>
                <Typography variant="h6"> Promised Volume: </Typography>
                <Typography variant="body"> {subOrder?.promised_volume || 0} </Typography>
              </CardContent>
            </Card>
            <Card sx={{ minWidth: 100, mx: 1, backgroundColor: '#AFD895', color: 'white' }}>
              <CardContent>
                <Typography variant="h6">  Remaining Volume: </Typography>
                <Typography variant="body"> {fNumber(remainingVolume)} </Typography>
              </CardContent>
            </Card>
          </Box>

          {assignment?.destination.value === CLIENT && (
            <FormControl fullWidth sx={{ my: 2 }}>
              <CustomSelect
                labelId="client-select-label"
                label={"Clients"}
                value={assignment?.clients.value}
                onChange={(e) => handleClientAssignment(e.target.value)}
                required={assignment?.clients.required}
                error={assignment?.clients.error}
                helperText={assignment?.clients.error && assignment?.clients.errorMessage}
              // disabled={isNotNullValue(subOrder?.supplier_id)}
              >
                <MenuItem value="" disabled>
                  <em>Select Client</em>
                </MenuItem>
                {clients?.map((client) => (
                  <MenuItem key={"client-order-" + client.suborder_id}
                    value={client.suborder_id}
                  >
                    <SupplierBox title="Client Name" minWidth={250}>{client.company_name}</SupplierBox>
                    <AvailableBox title="Outstaning Volume"> {fNumber(client.available_volume)}</AvailableBox>
                    {/* <AllocatedBox title="Allocated Volume">{fNumber(client.allocated_volume)}</AllocatedBox> */}
                    <OrderBox title="Order ID">{fNumber(client.suborder_id)}</OrderBox>
                  </MenuItem>
                ))}
              </CustomSelect>
            </FormControl>
          )}

          {assignment?.destination.value === 'depot' && (
            <>
              <FormControl fullWidth sx={{ my: 2 }}>
                <CustomSelect
                  labelId="depot-select-label"
                  label={"Depot"}
                  value={assignment?.depot.value}
                  onChange={(e) => handleDepotSelection(e.target.value)}
                  required={assignment?.depot.required}
                  error={assignment?.depot.error}
                  helperText={assignment?.depot.error && assignment?.depot.errorMessage}
                >
                  <MenuItem value="" disabled>
                    <em>Select Depot</em>
                  </MenuItem>
                  {depots?.map((d) => (
                    <MenuItem key={"depot-order-" + d.depot_id}
                      value={d.depot_id}
                    >
                      {d.depot_name}
                    </MenuItem>
                  ))}
                </CustomSelect>
              </FormControl>

              <FormControl fullWidth sx={{ my: 2 }}>
                <CustomSelect
                  labelId="from-tank-select-label"
                  label={"From Tank"}
                  value={assignment?.tank.value}
                  onChange={(e) => handleTankSelection(e.target.value)}
                  required={assignment?.tank.required}
                  error={assignment?.tank.error}
                  helperText={assignment?.tank.error && assignment?.tank.errorMessage}
                  disabled={tanks?.length < 1}
                >
                  <MenuItem value="" disabled>
                    <em>Select Tank</em>
                  </MenuItem>
                  {tanks?.map((t) => (
                    <MenuItem key={"from-tank-order-" + t.tank_id}
                      value={t.tank_id}
                    >
                      <SupplierBox title="Tank Label" minWidth={150}>{t.tank_label}</SupplierBox>
                      <CapacityBox  title="Tank Capacity"> {fNumber(t.capacity)}</CapacityBox>
                      <AllocatedBox title="Current Level"> {fNumber(t.available_volume)}</AllocatedBox>
                      <PromisedBox title="Promised Delivery"> {fNumber(t?.promised_volume|| 0)}</PromisedBox>
                      <AvailableBox title="Allowed Topup"> {fNumber(t?.effective_capacity|| 0)}</AvailableBox>
                    </MenuItem>
                  ))}
                </CustomSelect>
              </FormControl>

              <CustomTextField sx={{ my: 1 }}
                type="number"
                id="volume"
                variant="outlined"
                name='volume'
                label={`Volume`}
                value={(availableVolume <= 0 || remainingVolume <= 0) ? 0 : assignment?.volume.value}
                onChange={(e) => handleVolumeAssignment(e.target.value)}
                required={assignment?.volume.required}
                error={assignment?.volume.error}
                helperText={assignment?.volume.error && assignment?.volume.errorMessage}
              />

              <CustomTextField sx={{ my: 1 }}
                multiline
                id="note"
                variant="outlined"
                name='note'
                label={`Note`}
                onChange={(e) => handleInputChange("note", e.target.value)}
                value={assignment?.note.value}
                required={assignment?.note.required}
                error={assignment?.note.error}
                helperText={assignment?.note.error && assignment?.note.errorMessage}
              />
            </>
          )}
          <Grid container alignItems={'center'} sx={{ mt: 1, }}>
            <Grid item size={{ xs: 8.5 }} sx={{ my: 'auto', display: 'flex', justifyContent: 'left' }} >
              <FormControl error={assignment?.destination.error}>
                <RadioGroup
                  error
                  row
                  aria-labelledby="demo-row-radio-buttons-group-label"
                  name="destination"
                  onChange={(e) => handleDestinationSelection(e)}
                >
                  <RadioButton value="client" label="Client" />
                  <RadioButton value="depot" label="Depot" sx={{ ml: 2 }} />
                </RadioGroup>
                <FormHelperText>
                  {assignment?.destination.error && assignment?.destination.errorMessage}
                </FormHelperText>
              </FormControl>
            </Grid>
            <Grid item size={{ xs: 2 }} sx={{ my: 'auto', display: 'flex', alignContent: 'end' }}>
              <Button
                variant='contained'
                color="inherit"
                onClick={handleResetAndClose}
              >
                Cancel
              </Button>
              <LoadingButton
                sx={{ ml: 2, }}
                variant="contained"
                loading={submitted}
                onClick={handleSubmitAssignment}
              // disabled={isRemainingVolumeZero}
              >
                Submit
              </LoadingButton>
            </Grid>
          </Grid>
        </>
      </CustomModal>
    </Paper>
  )
}

AssignEnrouteTruck.propTypes = {

  /** Selected subOrder details */
  subOrder: PropTypes.object.isRequired,

  /** List of suitable clients */
  clients: PropTypes.array.isRequired,

  /** Flag to toggling openning of modal */
  openModal: PropTypes.bool,

  /** Callback function when closing modal */
  handleCloseModal: PropTypes.func.isRequired,

  /** Callback function after assignment of suborder to clients */
  handleSubmitCallback: PropTypes.func.isRequired,

}

export default AssignEnrouteTruck
